import { useQuery } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, ListTable, SelectField, type BadgeProps, type ListColumn } from "@wilco/ui";
import { formatDate, t, type MessageKey } from "../i18n.ts";
import { requestsQuery } from "./queries.ts";
import type { RequestState, RequestSummary, WarehouseCode } from "./types.ts";
import { ACTIVITY_WAREHOUSES } from "./warehouses.ts";

const route = getRouteApi("/app/stock/requests");

const STATE: Record<RequestState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  draft: { key: "requests.state.draft", tone: "neutral" },
  open: { key: "requests.state.open", tone: "info" },
  queued: { key: "requests.state.queued", tone: "strong" },
  done: { key: "requests.state.done", tone: "success" },
  cancelled: { key: "requests.state.cancelled", tone: "neutral" },
};

/** Заявки складов деятельности: открытые ждут резерва, в очереди ждут выдачи. */
export function RequestsPage() {
  const { warehouse } = route.useSearch();
  const navigate = useNavigate();
  const list = useQuery(requestsQuery(warehouse));

  const columns: ListColumn<RequestSummary>[] = [
    { key: "number", header: t("requests.number"), kind: "title", width: "minmax(0, 1fr)", render: (r) => <span className="w-num">{r.number}</span> },
    { key: "warehouse", header: t("requests.warehouse"), kind: "text", width: "9rem", render: (r) => r.warehouse.name },
    { key: "order", header: t("requests.order"), kind: "text", width: "7rem", render: (r) => r.order ?? "" },
    { key: "state", header: t("requests.state"), kind: "text", width: "8rem", render: (r) => <Badge tone={STATE[r.state].tone}>{t(STATE[r.state].key)}</Badge> },
    { key: "need", header: t("requests.need"), kind: "number", width: "6rem", render: (r) => formatDate(r.needDate) },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">{t("requests.title")}</h1>
        <Button asChild variant="primary">
          <Link to="/stock/requests/new" search={{ warehouse: warehouse ?? "SER" }}>
            {t("requests.reorder")}
          </Link>
        </Button>
      </div>
      <SelectField
        label={t("requests.filter")}
        value={warehouse ?? ""}
        onChange={(e) =>
          void navigate({ to: "/stock/requests", search: { warehouse: (e.target.value || undefined) as WarehouseCode | undefined }, replace: true })
        }
        options={[{ value: "", label: t("requests.all") }, ...ACTIVITY_WAREHOUSES.map((w) => ({ value: w.code, label: w.name }))]}
        className="requests-filter"
      />
      <ListTable
        label={t("requests.title")}
        columns={columns}
        rows={list.data ?? []}
        rowKey={(r) => r.id}
        empty={list.data ? t("requests.empty") : undefined}
        onRowClick={(r) => {
          if (r.state === "queued") void navigate({ to: "/stock/issue/$id", params: { id: r.id } });
        }}
      />
    </div>
  );
}
